/**
 * API Integration Module
 *
 * This module provides hooks for interacting with the backend API endpoints.
 * It uses TanStack Query (React Query) for data fetching and caching.
 *
 * @module lib/api
 */

import { useQuery, useMutation, useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useAuthStore } from "../store/useAuthStore";
import { useConnectedServicesStore } from "../store/useConnectedServicesStore";
import { toast } from "./use-toast";
import type {
  AccountLookupRequest,
  AccountLookupResponse,
  LoginRequest,
  LoginResponse,
  TokenRefreshRequest,
  TokenRefreshResponse,
  LogoutRequest,
  LogoutResponse,
  ApiResponse,
} from "../types/auth";
import { AUTO_LOGOUT_CODES } from "../types/auth";
import { encrypt } from "../utils/encryption";
import { axiosClient } from "./axiosClient";

/**
 * Error response interface
 */
interface ErrorResponse {
  object: string;
  status: string;
  message: string;
  timestamp: string;
  data: any;
  errors?: Array<{
    timestamp: string;
    code: string;
    field: string;
    message: string;
    ignore: string;
  }>;
}

export const handleApiError = (error: any, fallbackMessage: string = "An error occurred") => {
  console.error("API Error:", error);
  
  let errorMessage = fallbackMessage;
  
  try {
    if (error.response && error.response.data) {
      const errorData = error.response.data as ErrorResponse;
      
      if (errorData.errors && errorData.errors.length > 0) {
        errorMessage = errorData.errors[0].message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
  } catch (e) {
    console.error("Error parsing API error:", e);
  }
  
  toast({
    variant: "destructive",
    title: "Error",
    description: errorMessage,
  });
  
  return errorMessage;
};

/**
 * Global error handler for API responses
 * Displays toast notifications for API errors
 * 
 * @param {any} error - The error object from the API response
 * @param {string} fallbackMessage - Fallback message to display if error details are not available
 * @returns {void}
 */
const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    
    if (errorData.errors && errorData.errors.length > 0) {
      throw new Error(errorData.errors[0].message);
    }
    
    throw new Error(
      errorData.error || errorData.message || `Error: ${response.status}`
    );
  }
  return response.json();
};


/**
 * Gets the CSRF token from the XSRF-TOKEN cookie
 * Spring Security stores the CSRF token in this cookie
 */
const getCSRFToken = (): string | null => {
  const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

/**
 * Gets headers for API requests including Authorization and CSRF token
 *
 * @returns {Object} Headers object with Authorization Bearer token and CSRF token
 */
const getAuthHeader = (): Record<string, string> => {
  const { accessToken } = useAuthStore.getState();
  const headers: Record<string, string> = {};

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const csrfToken = getCSRFToken();
  if (csrfToken) {
    headers['X-XSRF-TOKEN'] = csrfToken;
  }

  return headers;
};

/**
 * Hook to fetch current user data
 * 
 * @returns {Object} Query object containing user data, loading state, and error
 * 
 * @example
 * const { data: userData, isLoading } = useUserData();
 * 
 * if (isLoading) {
 *   return <LoadingSpinner />;
 * }
 * 
 * return <div>Welcome, {userData.name}</div>;
 */
export const useUserData = () => {
  return useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      try {
        const response = await fetch("/api/user");
        if (response.status === 401) {
          // If unauthorized, return a standardized error object
          return { error: "Not authenticated", status: 401 };
        }
        return handleResponse(response);
      } catch (error) {
        console.error("Error fetching user data:", error);
        return { error: "Failed to fetch user data", status: 500 };
      }
    },
    // Only fetch when component mounts
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    // Ensure the query result is properly cached
    gcTime: 10 * 60 * 1000, // Cache for 10 minutes
  });
};

/**
 * Hook to import checklists
 * Provides a mutation function to upload and process checklist files
 * 
 * @returns {Object} Mutation object with mutate function and status
 * 
 * @example
 * const { mutate: importChecklist, isLoading } = useImportChecklists();
 * 
 * const handleFileUpload = (file) => {
 *   importChecklist(file, {
 *     onSuccess: (data) => {
 *       console.log('Import successful:', data);
 *     },
 *     onError: (error) => {
 *       console.error('Import failed:', error);
 *     }
 *   });
 * };
 */
export const useImportChecklists = () => {
  const mutation = useMutation({
    mutationFn: async (file: File) => {
      console.log('Calling checklist import API...');
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch("/api/checklists/import", {
        method: "POST",
        body: formData,
        headers: getAuthHeader(),
      });

      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }

      const data = await response.json();
      console.log('API call successful:', data);
      return data;
    },
  });

  // Wrap the mutate function to return a Promise that can be awaited
  const mutateAsync = async (file: File) => {
    return new Promise((resolve, reject) => {
      mutation.mutate(file, {
        onSuccess: (data) => resolve(data),
        onError: (error) => reject(error),
      });
    });
  };

  return {
    ...mutation,
    mutate: mutateAsync,
  };
};

/**
 * Hook to fetch connected services
 * Retrieves the list of external services connected to the application
 * and updates the connected services store
 * 
 * @returns {Object} Query object containing connected services data, loading state, and error
 * 
 * @example
 * const { data: connectedServices, isLoading, error } = useConnectedServices();
 * 
 * if (isLoading) {
 *   return <LoadingSpinner />;
 * }
 * 
 * if (error) {
 *   return <ErrorMessage error={error} />;
 * }
 * 
 * return (
 *   <ul>
 *     {connectedServices.map(service => (
 *       <li key={service.id}>{service.name}</li>
 *     ))}
 *   </ul>
 * );
 */
export const useConnectedServices = () => {
  const { setServices, setIsLoading, setError } = useConnectedServicesStore();

  return useQuery({
    queryKey: ["connectedServices"],
    queryFn: async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log('Calling connected services API...');
        const response = await fetch("/api/connected-services", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeader(),
          },
        });

        if (response.status === 401 || response.status === 302) {
          console.log("Authentication required for connected services");
          const errorObj = { error: "Not authenticated", status: response.status };
          setError("Not authenticated");
          return errorObj;
        }

        if (!response.ok) {
          throw new Error(`API returned status ${response.status}`);
        }

        const data = await response.json();
        
        // Update the store with the fetched services
        if (Array.isArray(data)) {
          setServices(data);
        }
        
        return data;
      } catch (error) {
        console.error("Error in connected services hook:", error);
        const errorMessage = "Failed to fetch connected services";
        setError(errorMessage);
        return { error: errorMessage };
      } finally {
        setIsLoading(false);
      }
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Cache for 10 minutes
  });
};

/**
 * Interface for workflow creation parameters
 */
export interface CreateWorkflowParams {
  type: string;
  code?: string;
  name?: string;
}

/**
 * Hook to create a new workflow
 * Provides a mutation function to create a new workflow
 * 
 * @returns {Object} Mutation object with mutate function and status
 * 
 * @example
 * const { mutate: createWorkflow, isPending } = useCreateWorkflow();
 * 
 * const handleCreateWorkflow = async () => {
 *   try {
 *     const data = await createWorkflow('ONTOLOGY');
 *     console.log('Workflow created:', data);
 *     navigate(`/workflows/${data.id}`);
 *   } catch (error) {
 *     console.error('Failed to create workflow:', error);
 *   }
 * };
 */
export const useCreateWorkflow = () => {
  const mutation = useMutation({
    mutationFn: async (params: string | CreateWorkflowParams) => {
      // Handle both string and object parameters for backward compatibility
      const workflowType = typeof params === 'string' ? params : params.type;
      const workflowCode = typeof params === 'string' ? undefined : params.code;
      const workflowName = typeof params === 'string' ? undefined : params.name;

      console.log('Calling workflow creation API...');
      // Prepare the request body
      const requestBody: any = { type: workflowType };

      // Add code and name if provided
      if (workflowCode) {
        requestBody.code = workflowCode;
      }

      if (workflowName) {
        requestBody.name = workflowName;
      }

      console.log('Creating workflow with:', requestBody);

      const response = await fetch('/api/workflows', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
      
      const data = await response.json();
      console.log('API call successful:', data);
      return data;
    },
  });

  // Wrap the mutate function to return a Promise that can be awaited
  const mutateAsync = async (params: string | CreateWorkflowParams) => {
    return new Promise((resolve, reject) => {
      mutation.mutate(params, {
        onSuccess: (data) => resolve(data),
        onError: (error) => reject(error),
      });
    });
  };

  return {
    ...mutation,
    mutate: mutateAsync,
  };
};

/**
 * Hook to compare selected entities (analyze mode)
 * Provides a mutation function to compare entities and show what would be migrated
 *
 * @returns {Object} Mutation object with mutate function and status
 *
 * @example
 * const { mutate: compareEntities, isLoading } = useCompareEntities();
 *
 * const handleCompare = async () => {
 *   const data = {
 *     databaseName: "db1",
 *     host: "instance1",
 *     checklistIds: ["123", "456"],
 *     serviceId: "1",
 *     facilityId: "123",
 *     targetServiceId: "2",
 *     targetFacilityId: "456"
 *   };
 *
 *   const result = await compareEntities(data);
 *   // Shows comparison results without actually importing
 * };
 */
export const useCompareEntities = () => {
  const mutation = useMutation({
    mutationFn: async (data: any) => {
      console.log('Calling entity comparison API...');
      const response = await fetch("/api/object-type-aggregation/compare", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader()
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }

      const responseData = await response.json();
      console.log('Comparison API call successful:', responseData);
      return responseData;
    }
  });

  // Wrap the mutate function to return a Promise that can be awaited
  const mutateAsync = async (data: any) => {
    return new Promise((resolve, reject) => {
      mutation.mutate(data, {
        onSuccess: (data) => resolve(data),
        onError: (error) => reject(error)
      });
    });
  };

  return {
    ...mutation,
    mutate: mutateAsync
  };
};

/**
 * Hook to import/migrate selected entities (actual migration)
 * Provides a mutation function to send selected entities to the server for actual import
 *
 * @returns {Object} Mutation object with mutate function and status
 *
 * @example
 * const { mutate: importEntities, isLoading } = useImportEntities();
 *
 * const handleConfirm = async () => {
 *   const data = {
 *     databaseName: "db1",
 *     host: "instance1",
 *     importData: selectedItems,
 *     code: "ONT-DEC25-01",
 *     name: "Migration Name",
 *     targetServiceId: "2",
 *     targetFacilityId: "456"
 *   };
 *
 *   await importEntities(data);
 *   // Actually performs the import
 * };
 */
export const useImportEntities = () => {
  const mutation = useMutation({
    mutationFn: async (data: any) => {
      console.log('Calling entity import API...');
      const response = await fetch("/api/checklists/import", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader()
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }

      const responseData = await response.json();
      console.log('Import API call successful:', responseData);
      return responseData;
    }
  });

  // Wrap the mutate function to return a Promise that can be awaited
  const mutateAsync = async (data: any) => {
    return new Promise((resolve, reject) => {
      mutation.mutate(data, {
        onSuccess: (data) => resolve(data),
        onError: (error) => reject(error)
      });
    });
  };

  return {
    ...mutation,
    mutate: mutateAsync
  };
};

/**
 * Interface for a checklist item
 */
export interface ChecklistItem {
  id: string;
  name: string;
  code: string;
  state: string;
  version: number;
  archived: boolean;
  properties: Array<{
    id: string;
    name: string;
    label: string;
    value: string;
  }>;
  audit: {
    createdAt: number;
    modifiedAt: number;
    createdBy: {
      id: string;
      employeeId: string;
      firstName: string;
      lastName: string;
    };
    modifiedBy: {
      id: string;
      employeeId: string;
      firstName: string;
      lastName: string;
    };
  };
  ancestorId: string;
  colorCode: string;
  global: boolean;
}

/**
 * Interface for paginated response
 */
export interface PaginatedResponse<T> {
  timestamp: number;
  object: string;
  status: string;
  message: string;
  data: T[];
  pageable: {
    page: number;
    pageSize: number;
    numberOfElements: number;
    totalPages: number;
    totalElements: number;
    first: boolean;
    last: boolean;
    empty: boolean;
  };
}

/**
 * Hook to fetch checklists from a client's endpoint
 * 
 * @param {Client | null} client - The client to fetch checklists from
 * @param {string | null} facilityId - The facility ID to use for filtering
 * @param {string | null} useCaseId - The use case ID to use for filtering
 * @returns {Object} Query object containing checklists data, loading state, and error
 */
export const useClientChecklists = (client: Client | null, facilityId: string | null, useCaseId: string | null, searchTerm: string = "") => {
  // First authenticate with the specific facility to get the token
  const { data: authData, isLoading: isAuthenticating, error: authError } = useAuthenticateWithFacility(client, facilityId);
  const token = authData?.token;
  
  return useQuery({
    queryKey: ["clientChecklists", client?.serviceId, facilityId, useCaseId, searchTerm],
    enabled: !!token && !!client?.endpoint && !!facilityId && !!useCaseId && !isAuthenticating,
    staleTime: 0, // Data is immediately considered stale
    gcTime: 0, // Data is immediately garbage collected
    queryFn: async () => {
      if (!token || !client?.endpoint || !facilityId || !useCaseId) {
        throw new Error("Token, endpoint, facilityId, and useCaseId are required to fetch checklists");
      }
      
      console.log('Fetching checklists from client endpoint:', client.endpoint);
      
      try {
        // Create the filters object for the API
        const fields = [
          {
            field: "archived",
            op: "EQ",
            values: [false]
          },
          {
            field: "state",
            op: "EQ",
            values: ["PUBLISHED"]
          },
          {
            field: "useCaseId",
            op: "EQ",
            values: [useCaseId]
          }
        ];
        
        // Add code filter if search term is provided
        if (searchTerm.trim()) {
          fields.push({
            field: "code",
            op: "LIKE",
            values: [searchTerm.trim()]
          });
        }
        
        const filters = {
          op: "AND",
          fields
        };
        
        // Build the URL with query parameters
        const url = new URL(`${client.endpoint}/v1/checklists`);
        url.searchParams.append('facilityId', facilityId);
        url.searchParams.append('page', '0');
        url.searchParams.append('size', '25');
        url.searchParams.append('sort', 'createdAt,desc');
        url.searchParams.append('filters', JSON.stringify(filters));
        
        // Make the checklists request
        const response = await fetch(url.toString(), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json, text/plain, */*",
            "Authorization": `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch checklists: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Checklists fetched successfully:', data.data?.length, 'items');
        return data;
      } catch (error) {
        console.error("Error fetching checklists:", error);
        throw error;
      }
    },
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch checklists with infinite scrolling and search
 * 
 * @param {string} searchTerm - The search term to filter checklists
 * @param {Client | null} client - The client to fetch checklists from
 * @param {string | null} facilityId - The facility ID to use for filtering
 * @param {string | null} useCaseId - The use case ID to use for filtering
 * @returns {Object} InfiniteQuery object with data, loading state, and fetch functions
 * 
 * @example
 * const { 
 *   data, 
 *   fetchNextPage, 
 *   hasNextPage, 
 *   isFetchingNextPage,
 *   isLoading
 * } = useInfiniteChecklists(searchTerm, selectedClient, selectedFacilityId, selectedUseCaseId);
 * 
 * // Render the checklists
 * return (
 *   <div>
 *     {data?.pages.map(page => 
 *       page.data.map(checklist => (
 *         <div key={checklist.id}>{checklist.name}</div>
 *       ))
 *     )}
 *     
 *     {hasNextPage && (
 *       <button 
 *         onClick={() => fetchNextPage()} 
 *         disabled={isFetchingNextPage}
 *       >
 *         {isFetchingNextPage ? 'Loading more...' : 'Load more'}
 *       </button>
 *     )}
 *   </div>
 * );
 */
export const useInfiniteChecklists = (searchTerm: string = "", client: Client | null = null, facilityId: string | null = null, useCaseId: string | null = null) => {
  // First authenticate with the specific facility to get the token
  const { data: authData, isLoading: isAuthenticating, error: authError } = useAuthenticateWithFacility(client, facilityId);
  const token = authData?.token;
  
  return useInfiniteQuery<PaginatedResponse<ChecklistItem>>({
    queryKey: ["infiniteChecklists", client?.serviceId, facilityId, useCaseId, searchTerm],
    enabled: !!token && !!client?.endpoint && !!facilityId && !!useCaseId && !isAuthenticating,
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<PaginatedResponse<ChecklistItem>> => {
      if (!token || !client?.endpoint || !facilityId || !useCaseId) {
        throw new Error("Token, endpoint, facilityId, and useCaseId are required to fetch checklists");
      }
      
      const page = pageParam as number;
      
      // Log the request details
      console.log('Fetching checklists - page:', page, 'search:', searchTerm, 'facilityId:', facilityId, 'useCaseId:', useCaseId);
      
      try {
        // Create the filters object for the API
        const fields = [
          {
            field: "archived",
            op: "EQ",
            values: [false]
          },
          {
            field: "state",
            op: "EQ",
            values: ["PUBLISHED"]
          },
          {
            field: "useCaseId",
            op: "EQ",
            values: [useCaseId]
          }
        ];
        
        // Add code filter if search term is provided
        if (searchTerm.trim()) {
          fields.push({
            field: "code",
            op: "LIKE",
            values: [searchTerm.trim()]
          });
        }
        
        const filters = {
          op: "AND",
          fields
        };
        
        // Build the URL with query parameters
        const url = new URL(`${client.endpoint}/v1/checklists`);
        url.searchParams.append('facilityId', facilityId);
        url.searchParams.append('page', page.toString());
        url.searchParams.append('size', '25');
        url.searchParams.append('sort', 'createdAt,desc');
        url.searchParams.append('filters', JSON.stringify(filters));
        
        // Make the API request with the bearer token
        const response = await fetch(url.toString(), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json, text/plain, */*",
            "Authorization": `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch checklists: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Checklists fetched successfully:', data.data?.length, 'items');
        return data;
      } catch (error) {
        console.error("Error fetching checklists:", error);
        throw error;
      }
    },
    getNextPageParam: (lastPage) => {
      // If this is the last page, return undefined to indicate no more pages
      if (lastPage.pageable.last) {
        return undefined;
      }
      // Otherwise, return the next page number
      return lastPage.pageable.page + 1;
    },
    staleTime: 0, // Data is immediately considered stale
    gcTime: 0, // Data is immediately garbage collected
    refetchOnMount: "always", // Always refetch when component mounts
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to generate a code for a specific code type
 * Provides a function to generate a code for a specific type (e.g., ONTOLOGY)
 * 
 * @returns {Object} Object containing the generateCode function
 * 
 * @example
 * const { generateCode, isGenerating } = useGenerateCode();
 * 
 * const handleGenerateCode = async () => {
 *   try {
 *     const data = await generateCode('ONTOLOGY');
 *     console.log('Generated code:', data.code);
 *     console.log('Generated name:', data.name);
 *   } catch (error) {
 *     console.error('Failed to generate code:', error);
 *   }
 * };
 */
export const useGenerateCode = () => {
  const mutation = useMutation({
    mutationFn: async (codeType: string) => {
      console.log('Generating code for type:', codeType);
      const response = await fetch(`/api/codes/generate?codeType=${codeType}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        }
      });
      
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Code generation successful:', data);
      return data;
    }
  });
  
  // Wrap the mutate function to return a Promise that can be awaited
  const generateCode = async (codeType: string) => {
    return new Promise((resolve, reject) => {
      mutation.mutate(codeType, {
        onSuccess: (data) => resolve(data),
        onError: (error) => reject(error)
      });
    });
  };
  
  return {
    generateCode,
    isGenerating: mutation.isPending
  };
};

/**
 * Hook for authentication operations
 * Provides functions for login and logout
 * 
 * @returns {Object} Object containing login and logout functions
 * 
 * @example
 * const { login, logout } = useLogin();
 * 
 * return (
 *   <div>
 *     <button onClick={login}>Login</button>
 *     <button onClick={logout}>Logout</button>
 *   </div>
 * );
 */
/**
 * Hook to process selected checklists
 * Provides a mutation function to process selected checklists and get analysis results
 * 
 * @returns {Object} Mutation object with mutate function and status
 * 
 * @example
 * const { mutate: processChecklists, isLoading } = useProcessChecklists();
 * 
 * const handleProcess = async () => {
 *   try {
 *     const data = await processChecklists({
 *       facilityId: "1616367801",
 *       checklistIds: ["123", "456"]
 *     });
 *     console.log('Processing successful:', data);
 *   } catch (error) {
 *     console.error('Processing failed:', error);
 *   }
 * };
 */
export const useProcessChecklists = () => {
  const mutation = useMutation({
    mutationFn: async (data: { facilityId: string; checklistIds: string[] }) => {
      console.log('Calling checklist processing API...');

      const response = await fetch("/api/checklists/process", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader()
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
      
      const responseData = await response.json();
      console.log('API call successful:', responseData);
      return responseData;
    }
  });
  
  // Wrap the mutate function to return a Promise that can be awaited
  const mutateAsync = async (data: { facilityId: string; checklistIds: string[] }) => {
    return new Promise((resolve, reject) => {
      mutation.mutate(data, {
        onSuccess: (data) => resolve(data),
        onError: (error) => reject(error)
      });
    });
  };
  
  return {
    ...mutation,
    mutate: mutateAsync
  };
};

/**
 * Hook to import checklist dependencies
 * Provides a function to fetch checklist dependencies via GET request
 * 
 * @returns {Object} Query object with fetch function and status
 * 
 * @example
 * const { fetchDependencies, isLoading } = useChecklistDependencyImport();
 * 
 * const handleImport = async () => {
 *   try {
 *     const data = await fetchDependencies({
 *       serviceId: "123",
 *       facilityId: "1616367801",
 *       checklistIds: ["123", "456"],
 *       databaseName: "production",
 *       hostId: "server-01"
 *     });
 *     console.log('Import successful:', data);
 *   } catch (error) {
 *     console.error('Import failed:', error);
 *   }
 * };
 */
export const useChecklistDependencyImport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchDependencies = async (data: {
    serviceId: string;
    facilityId: string;
    checklistIds: string[];
    databaseName: string;
    hostId: string;
    targetServiceId?: string;
    targetFacilityId?: string;
    targetAuthorIds?: string[];
    targetUseCaseId?: string;
  }) => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('Calling checklist-dependency import API with params:', data);

      // Build query parameters for GET request
      const params = new URLSearchParams();
      params.append('serviceId', data.serviceId);
      params.append('facilityId', data.facilityId);
      params.append('databaseName', data.databaseName);
      params.append('hostId', data.hostId);

      // Add checklistIds as multiple params
      data.checklistIds.forEach(id => {
        params.append('checklistIds', id);
      });

      // Add optional target parameters if provided
      if (data.targetServiceId) {
        params.append('targetServiceId', data.targetServiceId);
      }
      if (data.targetFacilityId) {
        params.append('targetFacilityId', data.targetFacilityId);
      }
      if (data.targetAuthorIds && data.targetAuthorIds.length > 0) {
        data.targetAuthorIds.forEach(authorId => {
          params.append('targetAuthorIds', authorId);
        });
      }
      if (data.targetUseCaseId) {
        params.append('targetUseCaseId', data.targetUseCaseId);
      }

      // Make the GET request to checklist-dependency import endpoint (for ontology migration)
      const response = await fetch(`/api/checklist-dependency/import?${params.toString()}`, {
        method: "GET",
        headers: getAuthHeader()
      });
      
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
      
      const responseData = await response.json();
      console.log('API call successful:', responseData);
      return responseData;
    } catch (err) {
      console.error("Error fetching checklist dependencies:", err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    fetchDependencies,
    isLoading,
    error
  };
};

/**
 * Hook to import checklists for process migration
 * Uses /api/checklists/import endpoint
 */
export const useChecklistImport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const importChecklists = async (data: {
    serviceId: string;
    facilityId: string;
    checklistIds: string[];
    databaseName: string;
    hostId: string;
    targetServiceId?: string;
    targetFacilityId?: string;
    targetAuthorIds?: string[];
    targetUseCaseId?: string;
  }) => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('Calling checklists import API with params:', data);

      // Create FormData for multipart/form-data request
      const formData = new FormData();

      // Add required parameters
      formData.append('serviceId', data.serviceId);
      formData.append('facilityId', data.facilityId);
      formData.append('databaseName', data.databaseName);
      formData.append('hostId', data.hostId);

      // Add checklistIds as array
      data.checklistIds.forEach(id => {
        formData.append('checklistIds', id);
      });

      // Add optional target parameters if provided
      if (data.targetServiceId) {
        formData.append('targetServiceId', data.targetServiceId);
      }
      if (data.targetFacilityId) {
        formData.append('targetFacilityId', data.targetFacilityId);
      }
      if (data.targetAuthorIds && data.targetAuthorIds.length > 0) {
        data.targetAuthorIds.forEach(authorId => {
          formData.append('targetAuthorIds', authorId);
        });
      }
      if (data.targetUseCaseId) {
        formData.append('targetUseCaseId', data.targetUseCaseId);
      }

      // Make the POST request to checklists import endpoint (for process migration)
      const response = await fetch('/api/checklists/import', {
        method: "POST",
        headers: getAuthHeader(),
        body: formData
      });

      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }

      const responseData = await response.json();
      console.log('API call successful:', responseData);
      return responseData;
    } catch (err) {
      console.error("Error importing checklists:", err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    importChecklists,
    isLoading,
    error
  };
};

/**
 * Hook to perform final process migration import
 * Uses /api/checklists/import endpoint with POST request and form data
 */
export const useProcessMigrationImport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const processImport = async (data: {
    checklistIds: string[];
    serviceId: string;
    facilityId: string;
    databaseName: string;
    hostId: string;
    targetServiceId: string;
    targetFacilityId: string;
    targetAuthorIds?: string;
    targetUseCaseId?: string;
    targetChecklistId?: string;
  }) => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('Calling process migration import API with params:', data);

      // Build FormData for POST request
      const formData = new FormData();
      data.checklistIds.forEach(id => formData.append('checklistIds', id));
      formData.append('serviceId', data.serviceId);
      formData.append('facilityId', data.facilityId);
      formData.append('databaseName', data.databaseName);
      formData.append('hostId', data.hostId);
      formData.append('targetServiceId', data.targetServiceId);
      formData.append('targetFacilityId', data.targetFacilityId);
      if (data.targetAuthorIds) {
        formData.append('targetAuthorIds', data.targetAuthorIds);
      }
      if (data.targetUseCaseId) {
        formData.append('targetUseCaseId', data.targetUseCaseId);
      }
      if (data.targetChecklistId) {
        formData.append('targetChecklistId', data.targetChecklistId);
      }

      const response = await fetch('/api/checklists/import', {
        method: "POST",
        headers: getAuthHeader(),
        body: formData
      });

      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }

      const responseData = await response.json();
      console.log('Process migration import API call successful:', responseData);
      return responseData;
    } catch (err) {
      console.error("Error in process migration import:", err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    processImport,
    isLoading,
    error
  };
};

/**
 * Authentication request interface
 */
export interface AuthRequest {
  clientId: string;
  clientSecret: string;
  facilityId: string;
}

/**
 * Authentication response interface
 */
export interface AuthResponse {
  timestamp: number;
  object: string;
  status: string;
  message: string;
  data: {
    accessToken: string;
    refreshToken: string;
    hasSetChallengeQuestion: boolean;
  };
}

/**
 * Use case interface
 */
export interface UseCase {
  id: string;
  name: string;
  label: string;
  orderTree: number;
  description: string | null;
  metadata: {
    "card-color": string;
  };
  enabled: boolean;
}

/**
 * Client interface based on the API response
 */
export interface Client {
  serviceId: number;
  serviceName: string;
  description: string;
  serviceType: string;
  version: string | null;
  endpoint: string;
  healthCheckPath: string | null;
  documentationUrl: string | null;
  customAuthConfig: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  serviceMapping: {
    mappingId: number;
    connectedServiceId: number;
    endpointPath: string;
    port: number;
    httpMethod: string;
    customHeaders: string | null;
    priority: number;
    active: boolean;
    createdAt: string;
    updatedAt: string;
  };
}

/**
 * Facility interface
 */
export interface Facility {
  id: string;
  name: string;
}

/**
 * Hook to fetch facilities
 * This is a temporary implementation with mock data until the real API is defined
 * 
 * @returns {Object} Query object containing facilities data, loading state, and error
 * 
 * @example
 * const { data: facilities, isLoading, error } = useFacilities();
 */
export const useFacilities = () => {
  return useQuery({
    queryKey: ["facilities"],
    queryFn: async () => {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Return mock facilities data
      // This will be replaced with a real API call when the endpoint is defined
      return [
        { id: "-1", name: "Global Portal" },
        { id: "1616367801", name: "Bangalore" },
        { id: "1616367803", name: "Delhi" },
        { id: "1643103502", name: "London" },
        { id: "1616367802", name: "Mumbai" },
        { id: "1643103501", name: "New York" },
        { id: "1643103503", name: "Sydney" }
      ] as Facility[];
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Cache for 10 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to get the current facility ID
 * This is a temporary implementation that returns a fixed ID until the real API is defined
 * 
 * @returns {string | null} The current facility ID or null
 */
export const useCurrentFacilityId = () => {
  return useQuery({
    queryKey: ["currentFacility"],
    queryFn: async () => {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Return a default facility ID
      // This will be replaced with a real API call when the endpoint is defined
      return "1616367801";
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Cache for 10 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch services for a connected service instance
 * 
 * @param {string | null} connectedServiceId - The ID of the connected service instance
 * @returns {Object} Query object containing services data, loading state, and error
 * 
 * @example
 * const { data: services, isLoading, error } = useServices("1");
 */
export const useServices = (connectedServiceId: string | null) => {
  return useQuery({
    queryKey: ["services", connectedServiceId],
    enabled: !!connectedServiceId, // Only run the query when connectedServiceId is provided
    queryFn: async () => {
      if (!connectedServiceId) {
        return [];
      }

      console.log('Fetching services for connected service:', connectedServiceId);

      try {
        // Make the API request
        const response = await fetch(`/api/service-mappings/services?connectedServiceId=${connectedServiceId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Accept": "*/*",
            ...getAuthHeader()
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch services: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Services fetched successfully:', data);
        return data as Client[];
      } catch (error) {
        console.error("Error fetching services:", error);
        handleApiError(error, "Failed to fetch services");
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Cache for 10 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to authenticate with a client's endpoint and get facilities from JWT
 * 
 * @param {Client | null} client - The client to authenticate with
 * @returns {Object} Query object containing authentication data, facilities, loading state, and error
 */
export const useAuthenticate = (client: Client | null) => {
  return useAuthenticateWithFacility(client, "-1"); // Always use Global Portal to get all facilities
};

/**
 * Hook to authenticate with a client's endpoint using a specific facility ID
 * 
 * @param {Client | null} client - The client to authenticate with
 * @param {string | null} facilityId - The facility ID to use for authentication
 * @returns {Object} Query object containing authentication data, loading state, and error
 */
export const useAuthenticateWithFacility = (client: Client | null, facilityId: string | null) => {
  return useQuery({
    queryKey: ["auth", client?.serviceId, facilityId, client?.endpoint],
    // Only enable the query when we have a client with an endpoint and a facilityId
    enabled: !!client && !!client.endpoint && !!facilityId,
    staleTime: 0, // Data is immediately considered stale
    gcTime: 0, // Data is immediately garbage collected
    refetchOnMount: "always", // Always refetch when component mounts
    queryFn: async () => {
      if (!client || !client.endpoint) {
        throw new Error("Client and endpoint are required for authentication");
      }
      
      console.log('Authenticating with client endpoint:', client.endpoint);
      
      try {
        // Parse customAuthConfig to get clientId and clientSecret
        let authConfig: { clientId: string; clientSecret: string } = { clientId: "1", clientSecret: "VW5pY29ybkAyMDI0" };
        
        if (client.customAuthConfig) {
          try {
            authConfig = JSON.parse(client.customAuthConfig);
          } catch (e) {
            console.error("Error parsing customAuthConfig:", e);
          }
        }
        
        // Prepare the request body with the provided facilityId
        const requestBody: AuthRequest = {
          clientId: authConfig.clientId,
          clientSecret: authConfig.clientSecret,
          facilityId: facilityId || "-1" // Default to Global Portal if facilityId is null
        };
        
        // Make the authentication request
        const response = await fetch(`${client.endpoint}/v1/auth/login`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Service-Id": "c6d8285b72a84efb8fbd608c7cada484"
          },
          body: JSON.stringify(requestBody)
        });
        
        // Use our handleResponse function to handle errors
        const data = await handleResponse(response) as AuthResponse;
        console.log('Authentication successful');
        
        // Import the JWT utilities
        const { parseJwt } = await import('../lib/jwt-utils');
        
        // Parse the JWT token to get the facilities
        const jwtPayload = parseJwt(data.data.accessToken);
        const facilities = jwtPayload?.facilities || [];
        
        // Return both the token and facilities
        return {
          token: data.data.accessToken,
          facilities: facilities
        };
      } catch (error: any) {
        console.error("Error authenticating:", error);
        
        // Show error toast
        handleApiError(error, "Authentication failed");
        
        throw error;
      }
    },
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch use cases from a client's endpoint
 * 
 * @param {Client | null} client - The client to fetch use cases from
 * @param {string | null} facilityId - The facility ID to use for authentication
 * @returns {Object} Query object containing use cases data, loading state, and error
 */
export const useUseCases = (client: Client | null, facilityId: string | null) => {
  // First authenticate with the specific facility to get the token
  const { data: authData, isLoading: isAuthenticating, error: authError } = useAuthenticateWithFacility(client, facilityId);
  const token = authData?.token;
  
  return useQuery({
    queryKey: ["useCases", client?.serviceId, facilityId],
    // Only enable the query when we have a client, facilityId, and we're not authenticating
    enabled: !!client && !!facilityId && !!client.endpoint && !!token && !isAuthenticating,
    staleTime: 0, // Data is immediately considered stale
    gcTime: 0, // Data is immediately garbage collected
    queryFn: async () => {
      if (!token || !client?.endpoint) {
        throw new Error("Token and endpoint are required to fetch use cases");
      }
      
      console.log('Fetching use cases from client endpoint:', client.endpoint);
      
      try {
        // Make the use cases request
        const response = await fetch(`${client.endpoint}/v1/use-cases`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch use cases: ${response.status}`);
        }
        
        const data = await response.json() as UseCase[];
        console.log('Use cases fetched successfully:', data);
        return data;
      } catch (error) {
        console.error("Error fetching use cases:", error);
        throw error;
      }
    },
    refetchOnWindowFocus: false,
  });
};

/**
 * Interface for workflow data
 */
export interface Workflow {
  id: number;
  code: string;
  name: string;
  description: string;
  workflowType: string;
  status: string;
  serviceId: number;
  serviceName: string;
  executionData: {
    facilityId: string;
    databaseName: string;
    facilityName: string;
  };
  createdBy: number;
  createdByEmail: string;
  createdAt: string;
  updatedAt: string;
  auditCount: number;
}

/**
 * Interface for workflow audit record
 */
export interface WorkflowAudit {
  id: number;
  workflowId: number;
  ontologyId: string;
  externalId: string | null;
  displayName: string;
  ontologyType: string;
  syncStatus: string;
  objectTypeName: string | null;
  referenceObjectTypeId: string;
  referencePropertyId: string | null;
  triggeredBy: number;
  triggeredByEmail: string;
  createdAt: string;
  processingResult: string;
  processingRemarks: string;
  errorMessage: string | null;
  processedAt: string;
}

/**
 * Interface for paginated response
 */
export interface PaginatedResponse<T> {
  data: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
}

/**
 * Hook to fetch workflows with pagination
 * 
 * @param {number} page - The page number (0-based)
 * @param {number} size - The page size
 * @param {string} sortBy - The field to sort by
 * @param {string} sortDirection - The sort direction ('Asc' or 'Desc')
 * @returns {Object} Query object containing workflows data, loading state, and error
 * 
 * @example
 * const { data, isLoading, error } = useWorkflows(0, 25, 'createdAt', 'Desc');
 */
export const useWorkflows = (
  page: number = 0, 
  size: number = 25, 
  sortBy: string = 'createdAt',
  sortDirection: string = 'Desc'
) => {
  return useQuery<PaginatedResponse<Workflow>>({
    queryKey: ['workflows', page, size, sortBy, sortDirection],
    queryFn: async () => {
      console.log('Fetching workflows - page:', page, 'size:', size);

      try {
        const url = `/api/workflows?page=${page}&size=${size}&sortBy=${sortBy}&sortDirection=${sortDirection}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch workflows: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Workflows fetched successfully:', data);
        return data;
      } catch (error) {
        console.error('Error fetching workflows:', error);
        throw error;
      }
    },
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Hook to fetch workflow audit records with pagination
 * 
 * @param {number} workflowId - The workflow ID
 * @param {number} page - The page number (0-based)
 * @param {number} size - The page size
 * @param {string} sortBy - The field to sort by
 * @param {string} sortDirection - The sort direction ('asc' or 'desc')
 * @returns {Object} Query object containing audit records data, loading state, and error
 * 
 * @example
 * const { data, isLoading, error } = useWorkflowAudits(123, 0, 25, 'id', 'desc');
 */
export const useWorkflowAudits = (
  workflowId: number | null,
  page: number = 0,
  size: number = 25,
  sortBy: string = 'id',
  sortDirection: string = 'desc'
) => {
  return useQuery<PaginatedResponse<WorkflowAudit>>({
    queryKey: ['workflowAudits', workflowId, page, size, sortBy, sortDirection],
    enabled: workflowId !== null,
    queryFn: async () => {
      if (workflowId === null) {
        throw new Error('Workflow ID is required to fetch audit records');
      }

      console.log('Fetching workflow audits - workflowId:', workflowId, 'page:', page);

      try {
        const url = `/api/workflows/${workflowId}/audits?page=${page}&size=${size}&sortBy=${sortBy}&sortDirection=${sortDirection}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch workflow audits: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Workflow audits fetched successfully:', data);
        return data;
      } catch (error) {
        console.error('Error fetching workflow audits:', error);
        throw error;
      }
    },
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Interface for a target user
 */
export interface TargetUser {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  employeeId: string;
}

/**
 * Hook to fetch users from a target connected service
 *
 * @param {string | null} targetServiceId - The target service ID
 * @param {string | null} targetFacilityId - The target facility ID
 * @param {string} searchTerm - The search term to filter users
 * @returns {Object} Query object containing users data, loading state, and error
 *
 * @example
 * const { data: users, isLoading, error } = useTargetUsers(targetServiceId, targetFacilityId, "john");
 */
export const useTargetUsers = (targetServiceId: string | null, targetFacilityId: string | null, searchTerm: string = "") => {
  return useQuery({
    queryKey: ["targetUsers", targetServiceId, targetFacilityId, searchTerm],
    // Only enable the query when we have both targetServiceId and targetFacilityId
    enabled: !!targetServiceId && !!targetFacilityId,
    queryFn: async () => {
      if (!targetServiceId || !targetFacilityId) {
        throw new Error("Target service ID and facility ID are required to fetch users");
      }

      console.log('Fetching target users - targetServiceId:', targetServiceId, 'targetFacilityId:', targetFacilityId, 'searchTerm:', searchTerm);

      try {
        // Build the URL with query parameters
        let url = `/api/target-users?targetServiceId=${targetServiceId}&targetFacilityId=${targetFacilityId}`;

        // Add filters parameter if search term is provided
        if (searchTerm && searchTerm.trim()) {
          const filters = {
            op: "OR",
            fields: [
              { field: "firstName", op: "LIKE", values: [searchTerm.trim()] },
              { field: "lastName", op: "LIKE", values: [searchTerm.trim()] },
              { field: "employeeId", op: "LIKE", values: [searchTerm.trim()] },
              { field: "email", op: "LIKE", values: [searchTerm.trim()] }
            ]
          };
          url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
        }

        // Make the API request
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            ...getAuthHeader()
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch target users: ${response.status}`);
        }

        const data = await response.json();
        console.log('Target users fetched successfully:', data);
        return data as TargetUser[];
      } catch (error) {
        console.error("Error fetching target users:", error);
        handleApiError(error, "Failed to fetch target users");
        throw error;
      }
    },
    staleTime: 0, // Data is immediately considered stale for search
    gcTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });
};

// ============================================================================
// TARGET SERVER CONFIG HOOKS (Using SERVER_CONFIG endpoint)
// ============================================================================

/**
 * Hook to fetch facilities from SERVER_CONFIG endpoint
 * Uses the configured DWI instance directly without requiring service selection
 *
 * @returns {Object} Query object containing facilities data, loading state, and error
 *
 * @example
 * const { data: facilities, isLoading, error } = useTargetFacilities();
 */
export const useTargetFacilities = () => {
  return useQuery({
    queryKey: ["targetFacilities"],
    queryFn: async () => {
      console.log('Fetching target facilities from SERVER_CONFIG endpoint');

      try {
        const response = await fetch('/api/target/facilities', {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeader()
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch target facilities: ${response.status}`);
        }

        const data = await response.json();
        console.log('Target facilities fetched successfully:', data);
        return data as Facility[];
      } catch (error) {
        console.error("Error fetching target facilities:", error);
        handleApiError(error, "Failed to fetch target facilities");
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Cache for 10 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch use cases from SERVER_CONFIG endpoint
 * Can be used with manual refetch for lazy loading
 *
 * @param {string | null} facilityId - The facility ID
 * @param {boolean} enabled - Whether to auto-fetch or wait for manual trigger
 * @returns {Object} Query object containing use cases data, loading state, error, and refetch function
 *
 * @example
 * const { data: useCases, isLoading, refetch } = useTargetUseCases(facilityId, false);
 * // Later, when dropdown is clicked:
 * refetch();
 */
export const useTargetUseCases = (facilityId: string | null, enabled: boolean = true) => {
  return useQuery({
    queryKey: ["targetUseCases", facilityId],
    enabled: enabled && !!facilityId,
    queryFn: async () => {
      if (!facilityId) {
        throw new Error("Facility ID is required to fetch use cases");
      }

      console.log('Fetching target use cases for facilityId:', facilityId);

      try {
        const response = await fetch(`/api/target/use-cases?facilityId=${facilityId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeader()
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch target use cases: ${response.status}`);
        }

        const data = await response.json();
        console.log('Target use cases fetched successfully:', data);
        return data as UseCase[];
      } catch (error) {
        console.error("Error fetching target use cases:", error);
        handleApiError(error, "Failed to fetch target use cases");
        throw error;
      }
    },
    staleTime: 0, // Data is immediately considered stale
    gcTime: 0, // Data is immediately garbage collected
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch published checklists from SERVER_CONFIG endpoint
 * Used for selecting which checklist to revise against
 *
 * @param {string | null} facilityId - The facility ID
 * @param {string | null} useCaseId - The use case ID
 * @returns {Object} Query object containing published checklists data, loading state, and error
 *
 * @example
 * const { data: checklists, isLoading, error } = useTargetPublishedChecklists(facilityId, useCaseId);
 */
export const useTargetPublishedChecklists = (facilityId: string | null, useCaseId: string | null) => {
  return useQuery({
    queryKey: ["targetPublishedChecklists", facilityId, useCaseId],
    enabled: !!facilityId && !!useCaseId,
    queryFn: async () => {
      if (!facilityId || !useCaseId) {
        throw new Error("Facility ID and Use Case ID are required to fetch published checklists");
      }

      console.log('Fetching target published checklists for facilityId:', facilityId, 'useCaseId:', useCaseId);

      try {
        const url = `/api/target/published-checklists?facilityId=${facilityId}&useCaseId=${useCaseId}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeader()
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch published checklists: ${response.status}`);
        }

        const data = await response.json();
        console.log('Target published checklists fetched successfully:', data);
        return data as any[];
      } catch (error) {
        console.error("Error fetching published checklists:", error);
        handleApiError(error, "Failed to fetch published checklists");
        throw error;
      }
    },
    staleTime: 0, // Data is immediately considered stale
    gcTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch users from SERVER_CONFIG endpoint
 *
 * @param {string | null} facilityId - The facility ID
 * @param {string} searchTerm - The search term to filter users
 * @returns {Object} Query object containing users data, loading state, and error
 *
 * @example
 * const { data: users, isLoading, error } = useTargetServerConfigUsers(facilityId, "john");
 */
export const useTargetServerConfigUsers = (facilityId: string | null, searchTerm: string = "") => {
  return useQuery({
    queryKey: ["targetServerConfigUsers", facilityId, searchTerm],
    enabled: !!facilityId,
    queryFn: async () => {
      if (!facilityId) {
        throw new Error("Facility ID is required to fetch users");
      }

      console.log('Fetching target users for facilityId:', facilityId, 'searchTerm:', searchTerm);

      try {
        let url = `/api/target/users?facilityId=${facilityId}`;

        // Add filters parameter if search term is provided
        if (searchTerm && searchTerm.trim()) {
          const filters = {
            op: "OR",
            fields: [
              { field: "firstName", op: "LIKE", values: [searchTerm.trim()] },
              { field: "lastName", op: "LIKE", values: [searchTerm.trim()] },
              { field: "employeeId", op: "LIKE", values: [searchTerm.trim()] },
              { field: "email", op: "LIKE", values: [searchTerm.trim()] }
            ]
          };
          url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
        }

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeader()
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch target users: ${response.status}`);
        }

        const data = await response.json();
        console.log('Target users fetched successfully:', data);
        return data as TargetUser[];
      } catch (error) {
        console.error("Error fetching target users:", error);
        handleApiError(error, "Failed to fetch target users");
        throw error;
      }
    },
    staleTime: 0, // Data is immediately considered stale for search
    gcTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });
};

// ============================================================================
// STREEM AUTHENTICATION HOOKS
// ============================================================================

/**
 * Hook to perform account lookup
 * Determines if user should use LOCAL or SSO authentication
 *
 * @example
 * const { mutateAsync: lookupAccount } = useAccountLookup();
 * const result = await lookupAccount({ username: 'user@example.com' });
 */
export const useAccountLookup = () => {
  return useMutation<ApiResponse<AccountLookupResponse>, Error, AccountLookupRequest>({
    mutationFn: async ({ username }) => {
      const response = await axiosClient.get<ApiResponse<AccountLookupResponse>>(
        `/v1/auth/account/lookup?username=${encodeURIComponent(username)}`
      );
      return response.data;
    },
  });
};

/**
 * Hook to get SSO redirect URL from utility_settings
 *
 * @example
 * const { mutateAsync: getSSORedirectUrl } = useGetSSORedirectUrl();
 * const result = await getSSORedirectUrl({ state: 'random-state', username: 'user@example.com' });
 */
export const useGetSSORedirectUrl = () => {
  return useMutation<ApiResponse<{ redirectUrl: string }>, Error, { state: string; username: string }>({
    mutationFn: async ({ state, username }) => {
      const response = await axiosClient.get<ApiResponse<{ redirectUrl: string }>>(
        `/v1/auth/sso/redirect-url?state=${encodeURIComponent(state)}&username=${encodeURIComponent(username)}`
      );
      return response.data;
    },
  });
};

/**
 * Hook to perform user login (LOCAL or SSO)
 *
 * @example
 * const { mutateAsync: login } = useStreemLogin();
 * const result = await login({
 *   username: 'user@example.com',
 *   password: 'encrypted_password',
 * });
 */
export const useStreemLogin = () => {
  return useMutation<ApiResponse<LoginResponse>, Error, LoginRequest>({
    mutationFn: async (credentials) => {
      const response = await axiosClient.post<ApiResponse<LoginResponse>>(
        "/v1/auth/login",
        credentials
      );
      return response.data;
    },
    onSuccess: (response) => {
      if (response.data) {
        const { login } = useAuthStore.getState();
        const userData = {
          id: response.data.id,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          roles: response.data.roles,
          facilities: response.data.facilities,
          settings: response.data.settings,
          licenses: response.data.licenses,
        };
        login(
          userData,
          response.data.accessToken,
          response.data.refreshToken,
          response.data.idToken
        );
      } else if (response.errors && response.errors.length > 0) {
        const error = response.errors[0];
        toast({
          variant: "destructive",
          title: "Login Failed",
          description: error.message,
        });
      }
    },
  });
};

/**
 * Hook to refresh access token
 * Called automatically when access token expires
 *
 * @example
 * const { mutateAsync: refreshToken } = useTokenRefresh();
 */
export const useTokenRefresh = () => {
  return useMutation<ApiResponse<TokenRefreshResponse>, Error, TokenRefreshRequest>({
    mutationFn: async ({ accessToken, refreshToken }) => {
      const response = await axiosClient.post<ApiResponse<TokenRefreshResponse>>(
        "/v1/auth/token/refresh",
        { accessToken, refreshToken }
      );
      return response.data;
    },
    onSuccess: (response) => {
      if (response.data) {
        const { setAccessToken } = useAuthStore.getState();
        setAccessToken(response.data.accessToken);
      }
    },
  });
};

/**
 * Hook to perform logout
 * Clears tokens and redirects user
 *
 * @example
 * const { mutateAsync: logout } = useStreemLogout();
 * await logout();
 */
export const useStreemLogout = () => {
  return useMutation<ApiResponse<LogoutResponse>, Error, LogoutRequest | void>({
    mutationFn: async (request) => {
      const { ssoIdToken = "" } = useAuthStore.getState();

      const response = await axiosClient.post<ApiResponse<LogoutResponse>>(
        "/v1/auth/logout",
        {
          idToken: request?.idToken || ssoIdToken,
        }
      );
      return response.data;
    },
    onSuccess: (response) => {
      const { logout } = useAuthStore.getState();
      logout();

      // Redirect to SSO logout URL if provided, otherwise to login
      if (response.data?.logoutUrl) {
        window.location.href = response.data.logoutUrl;
      } else {
        window.location.href = "/";
      }
    },
    onError: () => {
      // Even if logout fails on server, clear client state
      const { logout } = useAuthStore.getState();
      logout();
      window.location.href = "/";
    },
  });
};

/**
 * Legacy OAuth2 login hook (deprecated, use useStreemLogin instead)
 * Kept for backwards compatibility
 */
export const useLogin = () => {
  return {
    /**
     * Redirects to the OAuth2 login page
     */
    login: () => {
      window.location.href = "/v1/oauth2/authorization/azure?prompt=login";
    },

    /**
     * Logs the user out
     * Sends a logout request to the server and clears local auth state
     */
    logout: async () => {
      try {
        // Create a fetch request with Authorization header
        const response = await fetch("/v1/logout", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeader(),
          }
        });

        // If the logout request fails, log the error but continue with local logout
        if (!response.ok) {
          console.warn(
            "Server logout failed, proceeding with client-side logout"
          );
        }
      } catch (error) {
        console.error("Error during logout:", error);
      } finally {
        // Always clear local auth state and redirect regardless of server response
        useAuthStore.getState().logout();
        window.location.href = "/";
      }
    },
  };
};
